/**
 * Stage Conversion Costs — Mobile-first responsive
 * Mobile: stacked cards per stage with cost breakdown bar
 * Desktop: inline-edit table
 */
import { useState, useEffect } from 'react'
import {
  App, Alert, Button, Card, Col, Form, Grid, InputNumber, Modal, Row, Space,
  Statistic, Table, Tag, Typography,
} from 'antd'
import {
  SaveOutlined, EditOutlined, ThunderboltOutlined, TeamOutlined,
  HomeOutlined, CalculatorOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { wasteApi } from '@/features/waste/api'
import type { StageCost } from '@/features/waste/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'
import KPICard from '@/shared/components/KPICard'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text, Title } = Typography
const { useBreakpoint } = Grid

const STAGE_COLORS: Record<string, string> = {
  BLOWROOM: '#fa541c', CARDING: '#fa8c16', DRAWING: '#faad14',
  COMBING: '#a0d911', SIMPLEX: '#52c41a', RING: '#1677ff',
  WINDING: '#722ed1',
}

export default function StageCostsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const canAdmin = usePermission('waste.admin')

  const { data: costs = [], isLoading } = useQuery({
    queryKey: ['stage-costs', plantId],
    queryFn: () => wasteApi.listStageCosts(plantId as string),
    enabled: !!plantId,
  })

  // Inline edit state (desktop)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [tableForm] = Form.useForm()

  // Modal edit (mobile)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalCost, setModalCost] = useState<StageCost | null>(null)
  const [modalForm] = Form.useForm()

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: any) => wasteApi.updateStageCost(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stage-costs'] })
      message.success('Stage cost updated')
      setEditingId(null)
      setModalOpen(false)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const totalCycle = costs.reduce((s: number, c: StageCost) => s + Number(c.total_cost_per_kg || 0), 0)
  const heaviestStage = costs.reduce(
    (max: StageCost | null, c: StageCost) =>
      (max && Number(max.total_cost_per_kg) > Number(c.total_cost_per_kg)) ? max : c,
    null as StageCost | null,
  )

  const openModalEdit = (r: StageCost) => {
    setModalCost(r)
    modalForm.setFieldsValue({
      power_cost_per_kg: Number(r.power_cost_per_kg),
      labor_cost_per_kg: Number(r.labor_cost_per_kg),
      overhead_per_kg: Number(r.overhead_per_kg),
    })
    setModalOpen(true)
  }

  // ── Stage Card (mobile + tablet) ───────────────────────────────────────────
  function StageCard({ row }: { row: StageCost }) {
    const total = Number(row.total_cost_per_kg || 0)
    const power = Number(row.power_cost_per_kg || 0)
    const labor = Number(row.labor_cost_per_kg || 0)
    const oh    = Number(row.overhead_per_kg || 0)
    const color = STAGE_COLORS[row.stage] ?? '#8c8c8c'
    const pctOfCycle = totalCycle > 0 ? (total / totalCycle) * 100 : 0

    return (
      <Card
        size="small"
        style={{ marginBottom: 12, borderLeft: `4px solid ${color}` }}
        styles={{ body: { padding: 14 } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <Tag color="blue" style={{ fontSize: 13, fontWeight: 700, padding: '2px 10px' }}>{row.stage}</Tag>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
              {pctOfCycle.toFixed(1)}% of full cycle
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>TOTAL ₹/kg</Text>
            <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
              ₹{total.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Stacked cost bar */}
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
          {power > 0 && <div style={{ width: `${(power / total) * 100}%`, background: '#faad14' }} title={`Power ₹${power.toFixed(2)}`} />}
          {labor > 0 && <div style={{ width: `${(labor / total) * 100}%`, background: '#1677ff' }} title={`Labor ₹${labor.toFixed(2)}`} />}
          {oh > 0    && <div style={{ width: `${(oh / total) * 100}%`,    background: '#722ed1' }} title={`Overhead ₹${oh.toFixed(2)}`} />}
        </div>

        <Row gutter={8}>
          <Col span={8}>
            <Space size={4}><ThunderboltOutlined style={{ color: '#faad14' }} /><Text type="secondary" style={{ fontSize: 10 }}>POWER</Text></Space>
            <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>₹{power.toFixed(2)}</div>
          </Col>
          <Col span={8}>
            <Space size={4}><TeamOutlined style={{ color: '#1677ff' }} /><Text type="secondary" style={{ fontSize: 10 }}>LABOR</Text></Space>
            <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>₹{labor.toFixed(2)}</div>
          </Col>
          <Col span={8}>
            <Space size={4}><HomeOutlined style={{ color: '#722ed1' }} /><Text type="secondary" style={{ fontSize: 10 }}>O/H</Text></Space>
            <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>₹{oh.toFixed(2)}</div>
          </Col>
        </Row>

        {canAdmin && (
          <Button block icon={<EditOutlined />} style={{ marginTop: 12, height: 38 }}
            onClick={() => openModalEdit(row)}>
            Edit Costs
          </Button>
        )}
      </Card>
    )
  }

  // ── Desktop Table ─────────────────────────────────────────────────────────
  const columns = [
    { title: 'Stage', dataIndex: 'stage', width: 120,
      render: (v: string) => (
        <Tag color="blue" style={{ fontSize: 13, fontWeight: 700, padding: '4px 10px' }}>{v}</Tag>
      ) },
    {
      title: <Space><ThunderboltOutlined style={{ color: '#faad14' }}/>Power ₹/kg</Space>,
      dataIndex: 'power_cost_per_kg', align: 'right' as const, width: 140,
      render: (v: any, r: StageCost) => editingId === r.id
        ? <Form.Item name="power_cost_per_kg" style={{ margin: 0 }}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} size="middle" />
          </Form.Item>
        : <Text style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>₹{Number(v).toFixed(2)}</Text>,
    },
    {
      title: <Space><TeamOutlined style={{ color: '#1677ff' }}/>Labor ₹/kg</Space>,
      dataIndex: 'labor_cost_per_kg', align: 'right' as const, width: 140,
      render: (v: any, r: StageCost) => editingId === r.id
        ? <Form.Item name="labor_cost_per_kg" style={{ margin: 0 }}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} size="middle" />
          </Form.Item>
        : <Text style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>₹{Number(v).toFixed(2)}</Text>,
    },
    {
      title: <Space><HomeOutlined style={{ color: '#722ed1' }}/>Overhead ₹/kg</Space>,
      dataIndex: 'overhead_per_kg', align: 'right' as const, width: 150,
      render: (v: any, r: StageCost) => editingId === r.id
        ? <Form.Item name="overhead_per_kg" style={{ margin: 0 }}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} size="middle" />
          </Form.Item>
        : <Text style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>₹{Number(v).toFixed(2)}</Text>,
    },
    { title: 'Total', dataIndex: 'total_cost_per_kg', align: 'right' as const, width: 130,
      render: (v: any, r: StageCost) => (
        <div>
          <Text strong style={{ fontSize: 14, color: STAGE_COLORS[r.stage], fontVariantNumeric: 'tabular-nums' }}>
            ₹{Number(v).toFixed(2)}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 10 }}>
              {totalCycle > 0 ? `${((Number(v) / totalCycle) * 100).toFixed(1)}% cycle` : ''}
            </Text>
          </div>
        </div>
      ) },
    { title: 'Effective From', dataIndex: 'effective_from', width: 130,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Action', key: 'action', width: 130,
      render: (_: unknown, r: StageCost) => canAdmin && (
        editingId === r.id ? (
          <Space size={4}>
            <Button size="small" type="primary" icon={<SaveOutlined />}
              onClick={() => tableForm.validateFields().then(v => updateMutation.mutate({ id: r.id, data: v }))}>
              Save
            </Button>
            <Button size="small" onClick={() => setEditingId(null)}>X</Button>
          </Space>
        ) : (
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditingId(r.id)
            tableForm.setFieldsValue({
              power_cost_per_kg: Number(r.power_cost_per_kg),
              labor_cost_per_kg: Number(r.labor_cost_per_kg),
              overhead_per_kg: Number(r.overhead_per_kg),
            })
          }}>Edit</Button>
        )
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Stage Conversion Costs"
        subtitle="Per-stage ₹/kg cost basis. Drives waste reuse economics + cost-of-production."
      />

      {/* KPI Strip */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <KPICard
            label="Full Cycle Cost"
            value={totalCycle} prefix="₹" suffix=" /kg" precision={2}
            color="blue" icon={<CalculatorOutlined />}
            hint="Blowroom → Winding"
          />
        </Col>
        <Col xs={12} sm={12} md={8}>
          <KPICard
            label="Heaviest Stage"
            value={heaviestStage?.stage ?? '—'}
            color="red"
            icon={<ThunderboltOutlined />}
            hint={heaviestStage ? `₹${Number(heaviestStage.total_cost_per_kg).toFixed(2)}/kg` : ''}
          />
        </Col>
        <Col xs={12} sm={24} md={8}>
          <KPICard
            label="Active Stages"
            value={costs.length}
            color="green"
            icon={<CheckIcon />}
          />
        </Col>
      </Row>

      <Alert
        message="How this is used"
        description={
          <span style={{ fontSize: 13 }}>
            Each waste category's <Text strong>reprocessing cost</Text> is auto-computed by summing stage costs from the
            <Text strong> re-entry stage</Text> through Winding. E.g. <Tag color="blue">Drawing</Tag> waste re-enters at DRAWING →
            cost = Drawing + Combing + Simplex + Ring + Winding.
          </span>
        }
        type="info" showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Cost composition bar (whole cycle) */}
      {!isLoading && costs.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: 14 } }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Cost Distribution Across Stages</Text>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>(% of full cycle)</Text>
          </div>
          <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
            {costs.map((c: StageCost) => {
              const w = totalCycle > 0 ? (Number(c.total_cost_per_kg) / totalCycle) * 100 : 0
              if (w < 1) return null
              return (
                <div key={c.id} title={`${c.stage}: ₹${Number(c.total_cost_per_kg).toFixed(2)}/kg`}
                  style={{
                    width: `${w}%`, background: STAGE_COLORS[c.stage] ?? '#8c8c8c',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 11, fontWeight: 700, minWidth: 24,
                  }}>
                  {w >= 8 ? c.stage.slice(0, 3) : ''}
                </div>
              )
            })}
          </div>
          <Space wrap size={8}>
            {costs.map((c: StageCost) => (
              <Space key={c.id} size={4}>
                <span style={{ width: 8, height: 8, borderRadius: 4, background: STAGE_COLORS[c.stage], display: 'inline-block' }} />
                <Text style={{ fontSize: 11 }}>{c.stage}</Text>
              </Space>
            ))}
          </Space>
        </Card>
      )}

      {isMobile ? (
        <div>
          {isLoading ? <Card loading /> :
            costs.map((c: StageCost) => <StageCard key={c.id} row={c} />)
          }
        </div>
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Form form={tableForm}>
            <Table rowKey="id" columns={columns} dataSource={costs} loading={isLoading} size="middle" pagination={false} />
          </Form>
        </Card>
      )}

      {/* Mobile edit modal */}
      <Modal
        title={`Edit ${modalCost?.stage} Costs`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => modalForm.validateFields().then(v => updateMutation.mutate({ id: modalCost!.id, data: v }))}
        confirmLoading={updateMutation.isPending}
        okText="Save"
      >
        <Form form={modalForm} layout="vertical">
          <Form.Item name="power_cost_per_kg"
            label={<Space><ThunderboltOutlined style={{ color: '#faad14' }} />Power ₹/kg</Space>}
            rules={[{ required: true }]}>
            <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.1} />
          </Form.Item>
          <Form.Item name="labor_cost_per_kg"
            label={<Space><TeamOutlined style={{ color: '#1677ff' }} />Labor ₹/kg</Space>}
            rules={[{ required: true }]}>
            <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.1} />
          </Form.Item>
          <Form.Item name="overhead_per_kg"
            label={<Space><HomeOutlined style={{ color: '#722ed1' }} />Overhead ₹/kg</Space>}
            rules={[{ required: true }]}>
            <InputNumber size="large" style={{ width: '100%' }} min={0} step={0.1} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

// Inline check icon (since lucide is not in this project)
function CheckIcon() {
  return (
    <span style={{ fontSize: 14 }}>✓</span>
  )
}
